<template>
  <div class="preview-image" ref="viewer" v-loading="loading">
    <template v-if="isAvailable">
      <div class="preview-image__view">
        <div
          class="preview-image__view-inner scrollbar"
          ref="scrollbar"
          @mousedown="handleViewerMousedown">
          <img :src="previewSrc" :style="styles" draggable="false" width="auto" height="auto" ref="image" alt="Preview image">
        </div>      
      </div>
      <div v-if="isMobile" class="preview-image__footer" ref="footer">
        <div class="preview-action">
          <Download :file="previewFile" />
          <PreviewRotate @change="handleChangeRotate" />
        </div>
        <div class="app-download">
          <AppLink v-if="platform.isIos" type="apple" class="item" />
          <AppLink v-else type="google" class="item" />
        </div>
      </div>
      <div v-else class="preview-image__contrl">
        <div class="preview-image__contrl-inner">
          <Download :file="previewFile" />
          <PreviewScale :style="{paddingLeft: previewFile.downloadable ? null : '8px' }" @change="handleChangeScale" />
          <PreviewRotate @change="handleChangeRotate" />
        </div>
      </div>
    </template>
    <template v-else>
      <div class="shares-unavaiable">
        <span class="shares-unavaiable__text" v-text="$t('preview.msg_expired')"></span>
      </div>
    </template>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import Download from '@/components/download'
import AppLink from '@/components/app-link'
import { addEvent, removeEvent } from '@/utils/event'
import PreviewScale from '../components/scale'
import PreviewRotate from '../components/rotate'

export default {
  name: 'PreviewImage',
  components: {
    Download,
    AppLink,
    PreviewScale,
    PreviewRotate
  },
  props: {
    isMobile: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      loading: true,
      scale: 1,
      rotate: 0,
      imgSize: {
        width: 'auto',
        height: 'auto'
      },
      imgObject: null,
      isAvailable: true
    }
  },
  computed: {
    ...mapGetters([
      'platform',
      'previewFile'
    ]),
    previewSrc() {
      const { shareId } = this.$route.params

      return this.previewFile ? `/na-api/shares/${shareId}/files/${this.previewFile.id}/preview/` : ''
    },
    styles() {
      const { width, height } = this.imgSize
      
      return {
        transform: `rotate(${this.rotate})`,
        width: isNaN(width) ? null : `${this.scale * width}px`,
        height: isNaN(height) ? null : `${this.scale * height}px`
      }
    }
  },
  watch: {
    previewSrc() {
      this.loading = true
      this.imgObject = null
      this.init()
      this.reset()
    }
  },
  beforeDestroy() {
    removeEvent(window, 'resize', this.handleResize)
  },
  mounted() {
    this.handleResize()
    addEvent(window, 'resize', this.handleResize)
  },
  methods: {
    init() {
      if (this.previewSrc) {
        if (this.imgObject) {
          this.handleSetImage(this.imgObject)
        } else {
          const img = new Image()
          img.src = this.previewSrc

          img.onload = () => {
            this.loading = false
            this.imgObject = img
            this.isAvailable = true

            this.handleSetImage(img)
            this.$emit('loaded', true)
          }

          img.onerror = () => {
            this.loading = false
            this.isAvailable = false
          }
        }
      }

    },
    reset() {
      this.scale = 1
      this.rotate = 0
      this.isAvailable = true
      this.imgSize = {
        width: 'auto',
        height: 'auto'
      }
      this.$topic.publish('preview.reset')
    },
    handleChangeScale(scale) {
      this.scale = scale

      this.$nextTick(() => {
        const { scrollbar, image } = this.$refs
        const imageStyle = image.getBoundingClientRect()
        const scrollStyle = scrollbar.getBoundingClientRect()

        scrollbar.scrollTop = (imageStyle.height - scrollStyle.height) / 2
        scrollbar.scrollLeft = (imageStyle.width - scrollStyle.width) / 2
      })
    },
    handleChangeRotate(rotate) {
      this.rotate = rotate
    },
    handleSetImage(img) {
      const { viewer, footer } = this.$refs
      const footerHeight = footer ? footer.getBoundingClientRect().height : 0
      const wrapper = viewer.getBoundingClientRect()
      const maxWidth = wrapper.width
      const maxHeight = wrapper.height - footerHeight
      const widthRatio = maxWidth / img.width
      const heightRatio = maxHeight / img.height
      const ratio = Math.min(widthRatio, heightRatio)
      if (ratio < 1) {
        this.imgSize = {
          width: img.width * ratio,
          height: img.height * ratio
        }
      } else {
        this.imgSize = {
          width: img.width,
          height: img.height
        }
      }
    },
    handleResize() {
      this.reset()
      this.init()
    },
    handleViewerMousedown(evt) {
      const startX = evt.clientX
      const startY = evt.clientY
      const { scrollbar } = this.$refs
      
      scrollbar.style.cursor = 'move'

      document.onmousemove = evt => {
        const endX = evt.clientX
        const endY = evt.clientY

        scrollbar.scrollTop = scrollbar.scrollTop - (endY - startY) / 10
        scrollbar.scrollLeft = scrollbar.scrollLeft - (endX - startX) / 10
      }

      document.onmouseup = () => {
        document.onmousemove = null
        document.onmouseup = null
        scrollbar.style.cursor = null
      }
    }
  }
}
</script>

<style lang="less">
  @import url('~@/styles/var.less');
  .preview-image {
    position: relative;
    width: 100%;
    height: 100%;
    overflow: hidden;
    & &__view {
      display: flex;
      width: 100%;
      height: 100%;
      overflow: hidden;
      &-inner {
        display: inline-block;
        text-align: center;
        vertical-align: middle;
        width: 100%;
        height: 100%;
        img {
          object-fit: contain;
          display: block;
          margin: auto;
        }
      }
    }
    & &__contrl {
      position: absolute;
      left: 50%;
      bottom: 40px;
      transform: translateX(-50%);
      &-inner {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 4px;
        width: 298px;
        height: 48px;
        background-color: var(--background-contrl, @background-contrl);
        border-radius: 16px;
        overflow: hidden;
      }
    }
  }

  @media screen and (max-width: 1024px), screen and (max-height: 576px) {
    .preview-image {
      display: flex;
      align-items: center;
      justify-content: flex-start;
      flex-direction: column;
      & &__view {
        height: calc(100% - 86px);
      }      
      & &__footer {
        flex: 0 0 86px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100vw;
        padding: 0 16px 0 8px;
        .preview-action {
          display: flex;
          align-items: center;
          justify-content: flex-start;
        }
      }
      & &__contrl {
        display: none;
      }
    }
  }
</style>